(() => {
  'use strict';

  if (window.RMORubberMulchCalculator) {
    window.RMORubberMulchCalculator.refresh();
    return;
  }

  const rootSelector = '[data-rmo-mulch-calculator]';
  const amountToken = '__amount__';

  const replaceToken = (template, token, value) =>
    String(template || '').split(token).join(String(value));

  const calculateEstimate = (area, depthInches, packageConfig = {}) => {
    const numericArea = Number(area);
    const numericDepth = Number(depthInches);
    const cubicFeetPerBag = Number(packageConfig.cubicFeetPerBag);
    const fullPalletBags = Number.parseInt(packageConfig.fullPalletBags, 10);
    const halfPalletBags = Number.parseInt(packageConfig.halfPalletBags, 10);
    const requiredCubicFeet = numericArea * (numericDepth / 12);
    const packageConfigured =
      Number.isFinite(cubicFeetPerBag) &&
      cubicFeetPerBag > 0 &&
      Number.isInteger(fullPalletBags) &&
      fullPalletBags > 0;
    const hasHalfPallet =
      packageConfigured &&
      Number.isInteger(halfPalletBags) &&
      halfPalletBags > 0 &&
      halfPalletBags < fullPalletBags;
    const effectiveHalfPalletBags = hasHalfPallet ? halfPalletBags : 0;

    if (
      !Number.isFinite(numericArea) ||
      numericArea <= 0 ||
      !Number.isFinite(numericDepth) ||
      numericDepth <= 0
    ) {
      return null;
    }

    if (!packageConfigured) {
      return {
        requiredCubicFeet,
        packageConfigured: false
      };
    }

    const requiredBags = Math.ceil(requiredCubicFeet / cubicFeetPerBag);
    let fullPallets = Math.floor(requiredBags / fullPalletBags);
    const remainingBags = requiredBags % fullPalletBags;
    let halfPallets = 0;

    if (remainingBags > 0 && hasHalfPallet && remainingBags <= halfPalletBags) {
      halfPallets = 1;
    } else if (remainingBags > 0) {
      fullPallets += 1;
    }

    const includedBags =
      fullPallets * fullPalletBags + halfPallets * effectiveHalfPalletBags;
    const includedCubicFeet = includedBags * cubicFeetPerBag;

    return {
      requiredCubicFeet,
      packageConfigured: true,
      requiredBags,
      fullPallets,
      halfPallets,
      includedBags,
      includedCubicFeet,
      cubicFeetPerBag,
      fullPalletBags,
      halfPalletBags: effectiveHalfPalletBags
    };
  };

  const localizedCount = (count, oneTemplate, otherTemplate) =>
    replaceToken(count === 1 ? oneTemplate : otherTemplate, amountToken, count);

  const packageOrder = (root, estimate) => {
    const parts = [];

    if (estimate.fullPallets > 0) {
      parts.push(
        localizedCount(
          estimate.fullPallets,
          root.dataset.fullPalletOneTemplate,
          root.dataset.fullPalletOtherTemplate
        )
      );
    }

    if (estimate.halfPallets > 0) {
      parts.push(
        localizedCount(
          estimate.halfPallets,
          root.dataset.halfPalletOneTemplate,
          root.dataset.halfPalletOtherTemplate
        )
      );
    }

    return parts.join(root.dataset.packageSeparator || ' + ');
  };

  const setOutput = (root, name, value) => {
    const output = root.querySelector(`[data-rmo-mulch-output="${name}"]`);
    if (output) output.textContent = value;
  };

  const initialize = (root) => {
    if (root.dataset.rmoMulchInitialized === 'true') return;

    const areaInput = root.querySelector('[data-rmo-mulch-field="area"]');
    const depthInput = root.querySelector('[data-rmo-mulch-field="depth"]');
    const calculateButton = root.querySelector('[data-rmo-mulch-action="calculate"]');
    const errorElement = root.querySelector('[data-rmo-mulch-error]');
    const resultElement = root.querySelector('[data-rmo-mulch-result]');
    const packageDependentRows = root.querySelectorAll(
      '[data-rmo-package-dependent]'
    );

    if (
      !areaInput ||
      !depthInput ||
      !calculateButton ||
      !errorElement ||
      !resultElement
    ) {
      return;
    }

    const showError = (message, invalidInput) => {
      areaInput.removeAttribute('aria-invalid');
      depthInput.removeAttribute('aria-invalid');
      invalidInput.setAttribute('aria-invalid', 'true');
      errorElement.textContent = message;
      errorElement.hidden = false;
      resultElement.hidden = true;
      errorElement.focus({ preventScroll: true });
    };

    const calculate = () => {
      const area = Number.parseFloat(areaInput.value);
      const depthInches = Number.parseFloat(depthInput.value);

      if (!Number.isFinite(area) || area <= 0) {
        showError(root.dataset.areaError, areaInput);
        return;
      }

      if (!Number.isFinite(depthInches) || depthInches <= 0) {
        showError(root.dataset.depthError, depthInput);
        return;
      }

      const estimate = calculateEstimate(area, depthInches, {
        cubicFeetPerBag: root.dataset.cubicFeetPerBag,
        fullPalletBags: root.dataset.fullPalletBags,
        halfPalletBags: root.dataset.halfPalletBags
      });
      if (!estimate) return;

      areaInput.removeAttribute('aria-invalid');
      depthInput.removeAttribute('aria-invalid');
      errorElement.hidden = true;

      const requiredVolume = replaceToken(
        root.dataset.cubicFeetTemplate,
        amountToken,
        estimate.requiredCubicFeet.toFixed(1)
      );
      setOutput(root, 'required-volume', requiredVolume);

      if (estimate.packageConfigured) {
        const includedVolume = estimate.includedCubicFeet.toFixed(1);
        const totalProduct = replaceToken(
          root.dataset.includedVolumeTemplate,
          '__volume__',
          includedVolume
        );

        setOutput(root, 'package-order', packageOrder(root, estimate));
        setOutput(root, 'total-product', totalProduct);
        packageDependentRows.forEach((row) => {
          row.hidden = false;
        });
      } else {
        setOutput(root, 'package-order', root.dataset.packageUnavailable);
        packageDependentRows.forEach((row) => {
          row.hidden = true;
        });
      }

      resultElement.hidden = false;
      resultElement.focus({ preventScroll: true });
    };

    calculateButton.addEventListener('click', calculate);
    areaInput.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter') return;
      event.preventDefault();
      calculate();
    });
    depthInput.addEventListener('change', () => {
      const area = Number.parseFloat(areaInput.value);
      if (Number.isFinite(area) && area > 0) calculate();
    });

    root.dataset.rmoMulchInitialized = 'true';
  };

  const rootsWithin = (container = document) => {
    const roots = [];

    if (container.matches?.(rootSelector)) roots.push(container);
    container.querySelectorAll?.(rootSelector).forEach((root) => roots.push(root));

    return roots;
  };

  const refresh = (container = document) => {
    rootsWithin(container).forEach(initialize);
  };

  window.RMORubberMulchCalculator = {
    calculateEstimate,
    refresh
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => refresh(), { once: true });
  } else {
    refresh();
  }

  document.addEventListener('shopify:section:load', (event) => {
    refresh(event.target);
  });
})();
